import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Container, 
  Heading, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  Select, 
  useToast, 
  VStack, 
  CheckboxGroup, 
  Checkbox, 
  InputGroup, 
  InputRightElement,
  useClipboard,
  HStack,
  Text,
  Box,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  FormHelperText,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  Spinner,
  IconButton,
  Tooltip
} from '@chakra-ui/react'
import { ChevronRightIcon, CopyIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import apiClient from '../../../../utils/apiClient'

const EditApiKey = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [name, setName] = useState('')
  const [mode, setMode] = useState('sandbox')
  const [rateLimit, setRateLimit] = useState(1000)
  const [permissions, setPermissions] = useState(['read'])
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [showSecretKey, setShowSecretKey] = useState(false)

  const { hasCopied: apiKeyCopied, onCopy: onCopyApiKey } = useClipboard(apiKey)
  const { hasCopied: secretKeyCopied, onCopy: onCopySecretKey } = useClipboard(secretKey)

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0]

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await apiClient(`/api-keys/${tenantId}/${id}`)
        if (response.status === 'success') {
          const apiKeyData = response.data
          setName(apiKeyData.name)
          setMode(apiKeyData.mode)
          setRateLimit(apiKeyData.rate_limit)
          setPermissions(apiKeyData.permissions)
          setApiKey(apiKeyData.api_key)
          setSecretKey(apiKeyData.secret_key)
        } else {
          throw new Error('Failed to fetch API key')
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      } finally {
        setLoading(false)
      }
    }

    fetchApiKey()
  }, [tenantId, id, toast])

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const response = await apiClient(`/api-keys/${tenantId}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name,
          mode,
          rate_limit: rateLimit,
          permissions
        })
      })
      
      if (response.status === 'success') {
        toast({
          title: 'Success',
          description: 'API key updated successfully',
          status: 'success',
          duration: 3000
        })
        navigate('/settings/api-keys')
      } else {
        throw new Error('Failed to update API key')
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000
      })
    } finally {
      setIsSubmitting(false);
    }
  }

  const maskKey = (key, show) => {
    if (!key) return '';
    if (show) return key;
    return '*'.repeat(key.length - 4) + key.slice(-4);
  }

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading API key...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings">Settings</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/settings/api-keys">API Keys</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <HStack justify="space-between" align="center" mb={2}>
            <Heading size="lg">Edit API Key</Heading>
            <Badge colorScheme={mode === 'production' ? 'red' : 'blue'} variant="subtle">
              {mode}
            </Badge>
          </HStack>
          <Text color="gray.500">
            Update your API key settings. Changes will take effect immediately.
          </Text>
        </Box>

        {/* API Key Credentials Card */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Heading size="md">API Credentials</Heading>
              
              <FormControl>
                <FormLabel>API Key</FormLabel>
                <InputGroup size="md">
                  <Input
                    pr="8.5rem"
                    type={showApiKey ? "text" : "password"}
                    value={maskKey(apiKey, showApiKey)}
                    isReadOnly
                    fontFamily="monospace"
                    bg="gray.50"
                  />
                  <InputRightElement width="8.5rem">
                    <HStack spacing={1}>
                      <Tooltip label={showApiKey ? "Hide" : "Show"}>
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={() => setShowApiKey(!showApiKey)}
                          icon={showApiKey ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Copy">
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={onCopyApiKey}
                          icon={<CopyIcon />}
                          variant="ghost"
                        />
                      </Tooltip>
                    </HStack>
                  </InputRightElement>
                </InputGroup>
                <FormHelperText>
                  Use this key in your API requests
                </FormHelperText>
              </FormControl>
              
              <FormControl>
                <FormLabel>Secret Key</FormLabel>
                <InputGroup size="md">
                  <Input
                    pr="8.5rem"
                    type={showSecretKey ? "text" : "password"}
                    value={maskKey(secretKey, showSecretKey)}
                    isReadOnly
                    fontFamily="monospace"
                    bg="gray.50"
                  />
                  <InputRightElement width="8.5rem">
                    <HStack spacing={1}>
                      <Tooltip label={showSecretKey ? "Hide" : "Show"}>
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={() => setShowSecretKey(!showSecretKey)}
                          icon={showSecretKey ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                        />
                      </Tooltip>
                      <Tooltip label="Copy">
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={onCopySecretKey}
                          icon={<CopyIcon />}
                          variant="ghost"
                        />
                      </Tooltip>
                    </HStack>
                  </InputRightElement>
                </InputGroup>
                <FormHelperText>
                  Keep this secret key secure
                </FormHelperText>
              </FormControl>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Store these credentials securely. They provide access to your CMS data.
                </Text>
              </Alert>
            </VStack>
          </CardBody>
        </Card>

        {/* Configuration Card */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md">Configuration</Heading>
              
              <FormControl isRequired>
                <FormLabel>API Key Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
                <FormHelperText>
                  A descriptive name to help you identify this key
                </FormHelperText>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Environment Mode</FormLabel>
                <Select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </Select>
                <FormHelperText>
                  {mode === 'sandbox' 
                    ? 'Safe for development and testing'
                    : 'Live environment - use with caution'
                  }
                </FormHelperText>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Rate Limit (requests per minute)</FormLabel>
                <Input 
                  type="number" 
                  value={rateLimit} 
                  onChange={(e) => setRateLimit(parseInt(e.target.value))}
                  min="1"
                  max="10000"
                />
                <FormHelperText>
                  Maximum number of API requests allowed per minute
                </FormHelperText>
              </FormControl>

              <Divider />
              
              <FormControl isRequired>
                <FormLabel>Permissions</FormLabel>
                <CheckboxGroup colorScheme="blue" value={permissions} onChange={setPermissions}>
                  <VStack align="start" spacing={3}>
                    <Checkbox value="read" isChecked={permissions.includes('read')}>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Read</Text>
                        <Text fontSize="sm" color="gray.500">
                          View and retrieve data from the API
                        </Text>
                      </VStack>
                    </Checkbox>
                    <Checkbox value="write" isChecked={permissions.includes('write')}>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Write</Text>
                        <Text fontSize="sm" color="gray.500">
                          Create new content and data
                        </Text>
                      </VStack>
                    </Checkbox>
                    <Checkbox value="update" isChecked={permissions.includes('update')}>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Update</Text>
                        <Text fontSize="sm" color="gray.500">
                          Modify existing content and data
                        </Text>
                      </VStack>
                    </Checkbox>
                    <Checkbox value="delete" isChecked={permissions.includes('delete')}>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Delete</Text>
                        <Text fontSize="sm" color="gray.500">
                          Remove content and data (use with caution)
                        </Text>
                      </VStack>
                    </Checkbox>
                  </VStack>
                </CheckboxGroup>
                <FormHelperText>
                  Select the minimum permissions required for your application
                </FormHelperText>
              </FormControl>

              {/* Security Warning for Production */}
              {mode === 'production' && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">Production Mode Active</Text>
                    <Text fontSize="sm">
                      This API key has access to your live data. Ensure proper security measures are in place.
                    </Text>
                  </VStack>
                </Alert>
              )}

              <Divider />
              
              <HStack spacing={4} justify="flex-end">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/settings/api-keys')}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  colorScheme="blue" 
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  loadingText="Updating..."
                >
                  Update API Key
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}

export default EditApiKey